import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sparkles, Check, X, Trophy, Lock } from 'lucide-react';

const DailyQuestion = ({ user, language, translations: t, onXpEarned }) => {
  const [question, setQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [streak, setStreak] = useState(null);
  const [newBadge, setNewBadge] = useState(null); // Track newly unlocked badge

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const userId = user?.id || user?._id; // Support both id formats

  useEffect(() => {
    fetchDailyQuestion();
    if (userId) {
      fetchStreak();
    }
  }, [user, language]);

  const fetchDailyQuestion = async () => {
    try {
      const url = `${API_URL}/api/daily-question?language=${language}${userId ? `&user_id=${userId}` : ''}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setQuestion(data);
        if (data.already_answered) {
          setResult({
            correct: data.user_was_correct,
            correct_answer: data.correct_answer,
            already_answered: true
          });
        }
      }
    } catch (error) {
      console.error('Error fetching daily question:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStreak = async () => {
    try {
      const response = await fetch(`${API_URL}/api/daily-question/streak?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setStreak(data);
      }
    } catch (error) {
      console.error('Error fetching streak:', error);
    }
  };

  const handleAnswer = async (answer) => {
    if (result || submitting || !userId) return;
    
    setSelectedAnswer(answer);
    setSubmitting(true);

    // Store previous badge state
    const hadClarity = streak?.badges?.clarity?.earned;
    const hadWisdom = streak?.badges?.wisdom?.earned;

    try {
      const response = await fetch(`${API_URL}/api/daily-question/answer?user_id=${userId}&language=${language}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: question.question_id,
          answer: answer
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        if (onXpEarned) {
          onXpEarned(data.xp_earned);
        }
        
        // Refresh streak and check for new badges
        const streakResponse = await fetch(`${API_URL}/api/daily-question/streak?user_id=${userId}`);
        if (streakResponse.ok) {
          const newStreak = await streakResponse.json();
          setStreak(newStreak);
          
          // Check if new badge was earned
          if (!hadClarity && newStreak.badges?.clarity?.earned) {
            setNewBadge('clarity');
          } else if (!hadWisdom && newStreak.badges?.wisdom?.earned) {
            setNewBadge('wisdom');
          }
        }
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Translations for daily question
  const dailyT = {
    fr: {
      title: "Question du jour",
      subtitle: "Une minute pour élargir ta compréhension.",
      correctMessage: "+15 XP — Clarté renforcée.",
      incorrectMessage: "Observer est déjà apprendre.",
      alreadyAnswered: "Tu as déjà répondu aujourd'hui !",
      loginRequired: "Connecte-toi pour répondre",
      streakDays: "jours",
      nextBadge: "Prochain badge dans",
      badges: {
        clarity: { name: "Clarté", desc: "7 jours de pratique consécutifs", unlock: "Tu développes une pratique régulière." },
        wisdom: { name: "Sagesse", desc: "30 jours de pratique consécutifs", unlock: "La constance transforme la compréhension." }
      },
      newBadge: "Nouveau badge débloqué !"
    },
    en: {
      title: "Question of the Day",
      subtitle: "One minute to expand your understanding.",
      correctMessage: "+15 XP — Clarity reinforced.",
      incorrectMessage: "Observing is already learning.",
      alreadyAnswered: "You already answered today!",
      loginRequired: "Log in to answer",
      streakDays: "days",
      nextBadge: "Next badge in",
      badges: {
        clarity: { name: "Clarity", desc: "7 consecutive days of practice", unlock: "You're developing a regular practice." },
        wisdom: { name: "Wisdom", desc: "30 consecutive days of practice", unlock: "Consistency transforms understanding." }
      },
      newBadge: "New badge unlocked!"
    },
    it: {
      title: "Domanda del giorno",
      subtitle: "Un minuto per ampliare la tua comprensione.",
      correctMessage: "+15 XP — Chiarezza rafforzata.",
      incorrectMessage: "Osservare è già imparare.",
      alreadyAnswered: "Hai già risposto oggi!",
      loginRequired: "Accedi per rispondere",
      streakDays: "giorni",
      nextBadge: "Prossimo badge tra",
      badges: {
        clarity: { name: "Chiarezza", desc: "7 giorni consecutivi di pratica", unlock: "Stai sviluppando una pratica regolare." },
        wisdom: { name: "Saggezza", desc: "30 giorni consecutivi di pratica", unlock: "La costanza trasforma la comprensione." }
      },
      newBadge: "Nuovo badge sbloccato!"
    },
    es: {
      title: "Pregunta del día",
      subtitle: "Un minuto para ampliar tu comprensión.",
      correctMessage: "+15 XP — Claridad reforzada.",
      incorrectMessage: "Observar ya es aprender.",
      alreadyAnswered: "¡Ya respondiste hoy!",
      loginRequired: "Inicia sesión para responder",
      streakDays: "días",
      nextBadge: "Próximo badge en",
      badges: {
        clarity: { name: "Claridad", desc: "7 días consecutivos de práctica", unlock: "Estás desarrollando una práctica regular." },
        wisdom: { name: "Sabiduría", desc: "30 días consecutivos de práctica", unlock: "La constancia transforma la comprensión." }
      },
      newBadge: "¡Nuevo badge desbloqueado!"
    },
    pt: {
      title: "Pergunta do dia",
      subtitle: "Um minuto para expandir sua compreensão.",
      correctMessage: "+15 XP — Clareza reforçada.",
      incorrectMessage: "Observar já é aprender.",
      alreadyAnswered: "Você já respondeu hoje!",
      loginRequired: "Faça login para responder",
      streakDays: "dias",
      nextBadge: "Próximo badge em",
      badges: {
        clarity: { name: "Clareza", desc: "7 dias consecutivos de prática", unlock: "Você está desenvolvendo uma prática regular." },
        wisdom: { name: "Sabedoria", desc: "30 dias consecutivos de prática", unlock: "A constância transforma a compreensão." }
      },
      newBadge: "Novo badge desbloqueado!"
    },
    ar: {
      title: "سؤال اليوم",
      subtitle: "دقيقة واحدة لتوسيع فهمك.",
      correctMessage: "+15 XP — تم تعزيز الوضوح.",
      incorrectMessage: "الملاحظة هي بالفعل تعلم.",
      alreadyAnswered: "لقد أجبت بالفعل اليوم!",
      loginRequired: "سجل الدخول للإجابة",
      streakDays: "أيام",
      nextBadge: "الشارة التالية في",
      badges: {
        clarity: { name: "الوضوح", desc: "7 أيام متتالية من الممارسة", unlock: "أنت تطور ممارسة منتظمة." },
        wisdom: { name: "الحكمة", desc: "30 يومًا متتاليًا من الممارسة", unlock: "الثبات يحول الفهم." }
      },
      newBadge: "تم فتح شارة جديدة!"
    }
  };

  const dt = dailyT[language] || dailyT.fr;

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
        <CardContent className="p-6">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4">
              <div className="h-4 bg-cyan-200 rounded w-3/4"></div>
              <div className="h-4 bg-cyan-200 rounded w-1/2"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!question) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200 shadow-lg" data-testid="daily-question-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-cyan-600" />
            <CardTitle className="text-lg text-cyan-800">{dt.title}</CardTitle>
          </div>
          {streak && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-white">
                🔥 {streak.total_answered} {dt.streakDays}
              </Badge>
            </div>
          )}
        </div>
        <CardDescription className="text-cyan-600">{dt.subtitle}</CardDescription>
        
        {/* Badges earned display */}
        {streak && (streak.badges?.clarity?.earned || streak.badges?.wisdom?.earned) && (
          <div className="flex items-center gap-2 mt-2">
            {streak.badges?.clarity?.earned && (
              <div className="flex items-center gap-1 bg-gradient-to-r from-cyan-100 to-blue-100 px-3 py-1 rounded-full" title={dt.badges.clarity.desc}>
                <span>✨</span>
                <span className="text-sm font-medium text-cyan-700">{dt.badges.clarity.name}</span>
              </div>
            )}
            {streak.badges?.wisdom?.earned && (
              <div className="flex items-center gap-1 bg-gradient-to-r from-green-100 to-emerald-100 px-3 py-1 rounded-full" title={dt.badges.wisdom.desc}>
                <span>🌿</span>
                <span className="text-sm font-medium text-green-700">{dt.badges.wisdom.name}</span>
              </div>
            )}
          </div>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Question */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <p className="text-gray-800 font-medium">{question.question}</p>
        </div>

        {/* Options */}
        <div className="space-y-2">
          {question.options.map((option, index) => {
            const isSelected = selectedAnswer === option;
            const isCorrect = result && option === result.correct_answer;
            const isWrong = result && isSelected && !result.correct;

            let buttonClass = "w-full justify-start text-left h-auto py-3 px-4 ";
            
            if (result) {
              if (isCorrect) {
                buttonClass += "bg-green-100 border-green-500 text-green-800 hover:bg-green-100";
              } else if (isWrong) {
                buttonClass += "bg-red-100 border-red-500 text-red-800 hover:bg-red-100";
              } else {
                buttonClass += "bg-gray-50 text-gray-500 hover:bg-gray-50";
              }
            } else {
              buttonClass += isSelected 
                ? "bg-cyan-100 border-cyan-500 text-cyan-800" 
                : "bg-white hover:bg-cyan-50 border-gray-200";
            }

            return (
              <Button
                key={index}
                variant="outline"
                className={buttonClass}
                onClick={() => handleAnswer(option)}
                disabled={!!result || submitting || !userId}
                data-testid={`daily-option-${index}`}
              >
                <span className="flex items-center gap-2 w-full">
                  {result && isCorrect && <Check className="w-4 h-4 text-green-600" />}
                  {result && isWrong && <X className="w-4 h-4 text-red-600" />}
                  {option}
                </span>
              </Button>
            );
          })}
        </div>

        {/* Result message */}
        {result && (
          <div className={`rounded-lg p-4 text-center ${result.correct ? 'bg-green-100' : 'bg-amber-50'}`}>
            <p className={`font-medium ${result.correct ? 'text-green-800' : 'text-amber-800'}`}>
              {result.correct ? dt.correctMessage : dt.incorrectMessage}
            </p>
            {result.already_answered && (
              <p className="text-sm text-gray-600 mt-1">{dt.alreadyAnswered}</p>
            )}
          </div>
        )}

        {/* New badge unlocked notification */}
        {newBadge && (
          <div className="bg-gradient-to-r from-amber-100 to-yellow-100 rounded-lg p-4 text-center border border-amber-300 animate-pulse">
            <div className="text-2xl mb-2">{newBadge === 'clarity' ? '✨' : '🌿'}</div>
            <p className="font-bold text-amber-800">{dt.newBadge}</p>
            <p className="text-lg font-semibold text-amber-700">
              {newBadge === 'clarity' ? dt.badges.clarity.name : dt.badges.wisdom.name}
            </p>
            <p className="text-sm text-amber-600 mt-1">
              {newBadge === 'clarity' ? dt.badges.clarity.unlock : dt.badges.wisdom.unlock}
            </p>
          </div>
        )}

        {/* Login prompt */}
        {!userId && (
          <div className="bg-gray-100 rounded-lg p-4 text-center">
            <Lock className="w-5 h-5 mx-auto mb-2 text-gray-500" />
            <p className="text-gray-600 text-sm">{dt.loginRequired}</p>
          </div>
        )}

        {/* Streak progress */}
        {streak && streak.next_badge && streak.days_to_next > 0 && (
          <div className="text-center text-sm text-cyan-600">
            <Trophy className="w-4 h-4 inline mr-1" />
            {dt.nextBadge} {streak.days_to_next} {dt.streakDays}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyQuestion;
